import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery } from 'react-query';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import moment from 'moment';
import 'moment/locale/el';
import dynamicAPI from '../../api/dynamicAPI';
import '../../css/frontend/public-curation.css';

// Build the date/time as structured parts so the public page can render the
// same two-cell card view as the admin editor (a small "kicker" over a big
// value), read-only and without the admin's coloured borders.
const buildOneDay = (startVal, endVal, lang) => {
    const start = startVal ? moment(startVal).locale(lang) : null;
    const end = endVal ? moment(endVal).locale(lang) : null;
    if (!start && !end) { return null; }

    const hasTime = (m) => m && (m.hours() !== 0 || m.minutes() !== 0);
    const s = start || end;
    const e = end || start;
    const sameDay = s.isSame(e, 'day');

    let dateKicker;
    let dateMain;
    if (sameDay) {
        dateKicker = s.format('dddd');       // e.g. Saturday / Σάββατο
        dateMain = s.format('D MMM YYYY');   // e.g. 10 Oct 2026
    } else {
        dateKicker = `${s.format('ddd')} → ${e.format('ddd')}`;
        dateMain = `${s.format('D MMM')} → ${e.format('D MMM YYYY')}`;
    }

    let timeMain = '';
    if (start && end && sameDay && (hasTime(start) || hasTime(end))) {
        timeMain = `${start.format('HH:mm')} – ${end.format('HH:mm')}`;
    } else if (hasTime(s)) {
        timeMain = s.format('HH:mm');
    }

    return { dateKicker, dateMain, timeMain };
};

// Returns an array of day cards: the primary day (startDate/endDate) plus any
// additional days when the curation spans multiple days. null when no dates.
const buildDateTimes = (c, lang) => {
    const days = [];
    const primary = buildOneDay(c?.startDate, c?.endDate, lang);
    if (primary) { days.push(primary); }
    if (c?.multiDay && Array.isArray(c?.additionalDays)) {
        for (const d of c.additionalDays) {
            const day = buildOneDay(d?.startDate, d?.endDate, lang);
            if (day) { days.push(day); }
        }
    }
    return days.length ? days : null;
};

// Turn bare http(s) URLs inside free text into clickable links, leaving the
// rest of the text (and its line breaks) untouched.
const URL_RE = /(https?:\/\/[^\s<]+)/g;
const renderWithLinks = (text) => {
    if (!text) { return text; }
    const parts = [];
    let lastIndex = 0;
    let match;
    URL_RE.lastIndex = 0;
    while ((match = URL_RE.exec(text)) !== null) {
        const raw = match[0];
        const start = match.index;
        if (start > lastIndex) { parts.push(text.slice(lastIndex, start)); }
        // Keep trailing sentence punctuation out of the href.
        const trail = raw.match(/[.,;:!?)\]]+$/);
        const href = trail ? raw.slice(0, -trail[0].length) : raw;
        parts.push(
            <a key={start} href={href} target="_blank" rel="noopener noreferrer">
                {href}
            </a>
        );
        if (trail) { parts.push(trail[0]); }
        lastIndex = start + raw.length;
    }
    if (lastIndex < text.length) { parts.push(text.slice(lastIndex)); }
    return parts;
};

export default function PublicCuration() {
    const { t, i18n } = useTranslation();
    const lang = i18n.language && i18n.language.startsWith('el') ? 'el' : 'en';
    const { publicId, participantId } = useParams();
    const navigate = useNavigate();
    const [activeImg, setActiveImg] = useState(null);
    const [videoLoaded, setVideoLoaded] = useState(false);
    const [showReservation, setShowReservation] = useState(false);
    // Reservation form state.
    const [resName, setResName] = useState('');
    const [resPhone, setResPhone] = useState('');
    const [resSubmitting, setResSubmitting] = useState(false);
    const [resDone, setResDone] = useState(false);
    const [resError, setResError] = useState('');

    // Two shapes of the same page: the plain public link, and a per-participant
    // private link (…/p/<token>) that also carries that person's agreed price.
    const endpoint = participantId
        ? `/api/view/curations/${publicId}/p/${participantId}`
        : `/api/view/curations/${publicId}`;

    const { data, isLoading, isError } = useQuery(
        ['public-curation', publicId, participantId || ''],
        async () => await dynamicAPI('get', endpoint, {}),
        { retry: 0, enabled: !!publicId }
    );

    // An expired (or invalid) private link (…/p/<token>) is a dead end otherwise.
    // Instead of showing "not available", fall back to the plain public page:
    // it shows the published curation (without any private info such as the
    // agreed price / reservation), or its own "not available" if it isn't
    // published. The server 404s the same way for expired vs. wrong token, so we
    // simply hand off to the public page and let it decide.
    const redirecting = isError && !!participantId && !!publicId;
    useEffect(() => {
        if (redirecting) {
            navigate(`/view/curations/${publicId}`, { replace: true });
        }
    }, [redirecting, publicId, navigate]);

    const curation = data?.curation;
    const pricing = data?.pricing || null;
    // The single price to show: the participant's agreed price when set,
    // otherwise the workshop's global price.
    const hasAgreed = pricing && typeof pricing.agreedPrice === 'number';
    const hasRegular = pricing && typeof pricing.price === 'number';
    const payPrice = hasAgreed ? pricing.agreedPrice : (hasRegular ? pricing.price : null);
    // Discounted when a lower agreed price replaces the regular workshop price.
    const isDiscounted = hasAgreed && hasRegular && pricing.agreedPrice < pricing.price;
    // "Positions left" summary (only present when the curator enabled it).
    const spots = data?.spots || null;
    // Reservation (private participant link only, when the curator enabled it).
    const reservation = data?.reservation || null;

    const submitReservation = async (e) => {
        e.preventDefault();
        setResError('');
        const name = resName.trim();
        const phone = resPhone.trim();
        if (!name || !phone) {
            setResError(t('Please enter your name and phone number.'));
            return;
        }
        setResSubmitting(true);
        try {
            // On a per-participant private link submit to that participant's
            // endpoint; on the plain public / shareable link use the general one.
            const reserveEndpoint = participantId
                ? `/api/view/curations/${publicId}/p/${participantId}/reserve`
                : `/api/view/curations/${publicId}/reserve`;
            const res = await dynamicAPI('post', reserveEndpoint, { name, phone });
            if (res?.success) { setResDone(true); }
            else { setResError(res?.error || t('Something went wrong. Please try again.')); }
        } catch (err) {
            setResError(err?.error || t('Something went wrong. Please try again.'));
        } finally {
            setResSubmitting(false);
        }
    };
    const imagesPath = data?.images_path || '';
    const images = curation?.images || [];
    const hasTag = (img, tag) =>
        Array.isArray(img.tags) && img.tags.some((tg) => String(tg).toLowerCase() === tag);
    // Poster (top image): the Featured one wins, then the image tagged "poster",
    // then the first. Extras: only images tagged "extra public images". The
    // backend already strips the payload to these, this keeps the split
    // consistent.
    const poster =
        images.find((img) => img.featured) ||
        images.find((img) => hasTag(img, 'poster')) ||
        images[0] || null;
    const extras = images.filter(
        (img) => img !== poster && hasTag(img, 'extra public images')
    );
    // Only show the video when the curation allows it publicly (the backend
    // also strips it when off; this keeps the UI consistent either way).
    const video = curation?.showVideo === false ? null : curation?.video;
    const dateTimes = buildDateTimes(curation, lang);

    const formatMB = (bytes) => (bytes ? `${(bytes / (1024 * 1024)).toFixed(1)} MB` : '');

    // SEO / social preview values. The server also injects these into the HTML
    // for non-JS scrapers (Telegram/Facebook); this covers JS-capable ones and
    // client-side navigation (Google renders JS).
    const pageTitle = curation?.title || t('Curation');
    const metaDescription = (curation?.description || '')
        .replace(/\s+/g, ' ').trim().slice(0, 200);
    const ogImage = poster ? `${imagesPath}${poster.path}` : '';
    const pageUrl = typeof window !== 'undefined' ? window.location.href : '';

    return (
        <div className="container py-4 mobile-hamburger-clear" style={{ maxWidth: 820, margin: '0 auto' }}>
            <Helmet>
                <title>{pageTitle}</title>
                {metaDescription && <meta name="description" content={metaDescription} />}
                <meta property="og:type" content="article" />
                <meta property="og:site_name" content="SpaceTime Community Lab" />
                <meta property="og:title" content={pageTitle} />
                {metaDescription && <meta property="og:description" content={metaDescription} />}
                {pageUrl && <meta property="og:url" content={pageUrl} />}
                {ogImage && <meta property="og:image" content={ogImage} />}
                <meta name="twitter:card" content={ogImage ? 'summary_large_image' : 'summary'} />
                <meta name="twitter:title" content={pageTitle} />
                {metaDescription && <meta name="twitter:description" content={metaDescription} />}
                {ogImage && <meta name="twitter:image" content={ogImage} />}
            </Helmet>

            {isLoading || redirecting ? (
                <div className="text-center text-muted py-5">{t('Loading…')}</div>
            ) : isError || !curation ? (
                <div className="text-center py-5">
                    <h4 className="fw-light">{t('This page is not available.')}</h4>
                    <p className="text-muted">{t('The link may be turned off or no longer exist.')}</p>
                </div>
            ) : (
                <>
                    <h1 className="fw-light mb-2">{curation.title}</h1>
                    {dateTimes && dateTimes.map((dt, i) => (
                        // eslint-disable-next-line react/no-array-index-key
                        <div className="pc-datetime mb-3" key={i}>
                            <div className="pc-dt-cell">
                                <span className="pc-dt-kicker">{dt.dateKicker}</span>
                                <span className="pc-dt-main">{dt.dateMain}</span>
                            </div>
                            {dt.timeMain && (
                                <div className="pc-dt-cell">
                                    <span className="pc-dt-kicker">{t('Time')}</span>
                                    <span className="pc-dt-main">{dt.timeMain}</span>
                                </div>
                            )}
                        </div>
                    ))}

                    {Array.isArray(curation.tags) && curation.tags.length > 0 && (
                        <div className="d-flex flex-wrap gap-2 mb-3">
                            {curation.tags.map((tag) => (
                                <span key={tag} className="badge bg-light text-dark" style={{ fontSize: '0.8rem' }}>
                                    #{String(tag).replace(/\s+/g, '')}
                                </span>
                            ))}
                        </div>
                    )}

                    {poster && (
                        <div className="mb-4">
                            {(() => {
                                const src = `${imagesPath}${poster.path}`;
                                return (
                                    <img
                                        src={src}
                                        alt={curation.title}
                                        onClick={() => setActiveImg(src)}
                                        style={{
                                            width: '100%', borderRadius: 10, cursor: 'zoom-in',
                                            display: 'block', background: '#f1f3f5',
                                        }}
                                    />
                                );
                            })()}
                        </div>
                    )}

                    {curation.description && (
                        <p className="pc-description" style={{ whiteSpace: 'pre-wrap', lineHeight: 1.6, fontSize: '1.05rem' }}>
                            {renderWithLinks(curation.description)}
                        </p>
                    )}

                    {(spots || payPrice != null) && (
                        <div className="pc-info">
                            {spots && (
                                <div className="pc-info-cell pc-info-spots">
                                    {spots.full ? (
                                        // The curator flipped the manual "Full" switch: show it under
                                        // the same "Spots left" label, with an apology note beside it.
                                        <>
                                            <span className="pc-info-label">{t('Spots left')}</span>
                                            <span className="pc-info-value pc-info-full">
                                                {t('Full')}
                                                <small
                                                    className="pc-info-full-note"
                                                    style={{ marginLeft: 8, fontWeight: 400, fontSize: '0.6em', color: '#8a8a8a' }}
                                                >
                                                    {t('no more spots left')}
                                                </small>
                                            </span>
                                        </>
                                    ) : (
                                        <>
                                            <span className="pc-info-label">{t('Spots left')}</span>
                                            {/* The public count is intentionally always "1" (never
                                                the real remaining number) unless marked full above. */}
                                            <span className="pc-info-value">{Math.max(1, spots.left)}</span>
                                        </>
                                    )}
                                </div>
                            )}
                            {payPrice != null && (
                                <div className="pc-info-cell">
                                    <span className="pc-info-label">{t('Price')}</span>
                                    <span className="pc-info-value">
                                        {payPrice === 0 ? (
                                            t('FREE')
                                        ) : (
                                            <>
                                                {isDiscounted && <span className="pc-info-old">€{pricing.price}</span>}
                                                €{payPrice}
                                            </>
                                        )}
                                    </span>
                                </div>
                            )}
                        </div>
                    )}

                    {reservation?.textEnabled && (
                        <div className="pc-reserve">
                            {!showReservation ? (
                                <button
                                    type="button"
                                    className="pc-reserve-btn"
                                    onClick={() => setShowReservation(true)}
                                >
                                    {t('Reserve spot')}
                                </button>
                            ) : (
                                <div className="pc-reserve-panel">
                                    <h3 className="pc-reserve-title">{t('How to reserve your spot')}</h3>
                                    {reservation.instructions ? (
                                        <p className="pc-reserve-text">{renderWithLinks(reservation.instructions)}</p>
                                    ) : (
                                        <p className="pc-reserve-text pc-reserve-text--muted">
                                            {t('Reservation instructions will be shared soon.')}
                                        </p>
                                    )}
                                </div>
                            )}
                        </div>
                    )}

                    {reservation?.formEnabled && (
                        <div className="pc-reserve">
                            {resDone ? (
                                <div className="pc-reserve-panel">
                                    <h3 className="pc-reserve-title">{t('Reservation received')}</h3>
                                    <p className="pc-reserve-text">{t('Thanks! We will contact you within 24 hours.')}</p>
                                </div>
                            ) : (
                                <form className="pc-reserve-form" onSubmit={submitReservation}>
                                    {spots?.full ? (
                                        <>
                                            <h3 className="pc-reserve-title">{t('Reserve your spot for the next one')}</h3>
                                            <p className="pc-reserve-text pc-reserve-text--muted" style={{ fontSize: '0.85rem', marginTop: '-6px' }}>
                                                {t('we will call you in case someone cancels')}
                                            </p>
                                        </>
                                    ) : (
                                        <h3 className="pc-reserve-title">{t('Reserve your spot')}</h3>
                                    )}
                                    <div className="pc-reserve-fields">
                                        <input
                                            type="text"
                                            className="pc-reserve-input"
                                            placeholder={t('Your name')}
                                            value={resName}
                                            onChange={(e) => setResName(e.target.value)}
                                            maxLength={120}
                                            required
                                        />
                                        <input
                                            type="tel"
                                            className="pc-reserve-input"
                                            placeholder={t('Your phone')}
                                            value={resPhone}
                                            onChange={(e) => setResPhone(e.target.value)}
                                            maxLength={40}
                                            required
                                        />
                                    </div>
                                    <button type="submit" className="pc-reserve-btn" disabled={resSubmitting}>
                                        {resSubmitting ? t('Sending…') : t('Reserve spot')}
                                    </button>
                                    {resError && <p className="pc-reserve-error">{resError}</p>}
                                    {reservation.curatorPhone && (
                                        <p className="pc-reserve-call">
                                            {t('or call this number')}:{' '}
                                            <a href={`tel:${reservation.curatorPhone}`}>{reservation.curatorPhone}</a>
                                        </p>
                                    )}
                                </form>
                            )}
                        </div>
                    )}

                    {(extras.length > 0 || video) && (
                        <div className="mt-4 pc-extras">
                            {extras.map((img) => {
                                const src = `${imagesPath}${img.path}`;
                                return (
                                    <img
                                        key={img.id || img.path}
                                        className="pc-extra"
                                        src={src}
                                        alt={curation.title}
                                        loading="lazy"
                                        onClick={() => setActiveImg(src)}
                                    />
                                );
                            })}

                            {video && (
                                videoLoaded ? (
                                    <video
                                        className="pc-video"
                                        src={`${imagesPath}${video.path}`}
                                        controls
                                        autoPlay
                                    />
                                ) : (
                                    <button
                                        type="button"
                                        className="pc-video-btn"
                                        onClick={() => setVideoLoaded(true)}
                                    >
                                        <span style={{ fontSize: '2.5rem', lineHeight: 1 }}>▶</span>
                                        <span style={{ fontSize: '0.8rem', opacity: 0.8 }}>
                                            {t('Click to play')}{video.size ? ` · ${formatMB(video.size)}` : ''}
                                        </span>
                                    </button>
                                )
                            )}
                        </div>
                    )}

                    {activeImg && (
                        <div
                            onClick={() => setActiveImg(null)}
                            style={{
                                position: 'fixed', inset: 0, background: 'rgba(0,0,0,.8)',
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                zIndex: 9999, padding: 16, cursor: 'zoom-out',
                            }}
                        >
                            <img
                                src={activeImg}
                                alt=""
                                style={{ maxWidth: '95vw', maxHeight: '95vh', width: 'auto', height: 'auto', objectFit: 'contain', borderRadius: 8 }}
                            />
                        </div>
                    )}
                </>
            )}
        </div>
    );
}
